// OperatorLicensingPage.jsx
import React, { Fragment, useMemo, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform
} from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Bot,
  Boxes,
  Building2,
  Check,
  ChevronDown,
  CircleDollarSign,
  Database,
  Gamepad2,
  Gauge,
  Gem,
  Globe2,
  Layers3,
  LockKeyhole,
  Network,
  Rocket,
  Scale,
  ServerCog,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  WalletCards,
  Waypoints,
  Zap
} from "lucide-react";
import "./Styles.css";
import Footer from "../Universal/Footer/Index";
import HeaderToMainPage from "../Universal/Header/Index";
import { Link } from "react-router-dom";

const COMMERCIAL_MODEL = {
  revenueShareMin: 15,
  revenueShareMax: 30,
  additionalGameMonthly: 500
};

const pricingTiers = [
  {
    id: "starter",
    number: "01",
    name: "Starter",
    label: "SINGLE-BRAND ENTRY",
    icon: Rocket,
    accent: "cyan",
    summary: "For a first operator defining its initial catalog, staffing responsibilities and recurring software budget.",
    positioning: "A narrower opening package keeps optional modules and game expansion out of the initial quote. The operator can request additional access after the launch scope is understood.",
    monthly: "Quoted",
    games: "Starter game package",
    support: "Standard",
    brands: "1 tenant",
    access: "Core platform",
    features: [
      "One branded operator environment",
      "A defined entry-level game selection",
      "Player account and cashier workflow access",
      "Essential game and transaction administration",
      "Core reporting and incident history",
      "Initial configuration and onboarding assistance",
      "Platform updates within the licensed scope",
    ],
    goodFor: [
      "First-time operator teams",
      "One-brand launch proposals",
      "Projects requiring a bounded initial quote",
    ],
  },
  {
    id: "standard",
    number: "02",
    name: "Standard",
    label: "ONGOING OPERATIONS",
    icon: Layers3,
    accent: "green",
    featured: true,
    summary: "For teams with an established launch plan and the need for broader day-to-day operating tools.",
    positioning: "The step up is practical: more included content, more campaign capabilities, and additional reporting for a growing team—not a different software foundation.",
    monthly: "Quoted",
    games: "Expanded game package",
    support: "Enhanced",
    brands: "1 tenant",
    access: "Expanded control plane",
    features: [
      "Starter coverage plus an expanded catalog",
      "Promotion and reward management options",
      "More detailed player and activity reporting",
      "Additional campaign and catalog settings",
      "Expanded operational review views",
      "Broader access for internal teams",
      "Enhanced onboarding and support scope",
    ],
    goodFor: [
      "Operators preparing consistent campaigns",
      "Teams handling regular player volume",
      "One-brand businesses needing deeper reporting",
    ],
  },
  {
    id: "pro",
    number: "03",
    name: "Pro",
    label: "DEEPER CAPABILITIES",
    icon: Zap,
    accent: "violet",
    summary: "For operators evaluating advanced content, more demanding operations and additional specialist tooling.",
    positioning: "Pro packages capabilities that warrant operational ownership. Clarify which advanced games, intelligence features and service commitments the business needs before quoting them.",
    monthly: "Quoted",
    games: "Large game package",
    support: "Priority",
    brands: "1–multiple",
    access: "Advanced platform",
    features: [
      "Standard coverage plus a larger catalog",
      "Access to eligible tournament and advanced games",
      "More detailed treasury and exception review",
      "AI-assisted operator analysis where enabled",
      "Advanced operational reporting options",
      "Expansion paths for additional experiences",
      "Priority support scope as contracted",
    ],
    goodFor: [
      "Businesses adding specialist staff",
      "Projects evaluating tournaments or live features",
      "Operators planning more than one brand",
    ],
  },
  {
    id: "enterprise",
    number: "04",
    name: "Enterprise",
    label: "NEGOTIATED DEPLOYMENT",
    icon: Building2,
    accent: "pink",
    summary: "For operators whose deployment cannot be priced responsibly from a standard package alone.",
    positioning: "Scope the technical and commercial commitments together: brands, hosting topology, integrations, security review, rollout milestones and agreed support levels.",
    monthly: "Custom",
    games: "Custom / broad catalog",
    support: "Dedicated",
    brands: "Multi-brand",
    access: "Full / negotiated",
    features: [
      "Custom catalog and tenant count in writing",
      "Multi-brand organizational requirements",
      "Dedicated infrastructure options if agreed",
      "Payment, KYC/KYB and partner integration scoping",
      "Security and performance evaluation",
      "Any bespoke development expressly specified",
      "Dedicated support and SLA terms if contracted",
    ],
    goodFor: [
      "Multi-brand operating groups",
      "Integration-intensive deployments",
      "Buyers requiring negotiated service terms",
    ],
  },
];

const revenueLayers = [
  {
    id: "platform",
    number: "01",
    icon: ServerCog,
    accent: "cyan",
    label: "SOFTWARE ACCESS",
    title: "Specify what the recurring license includes.",
    body: "The monthly license is quoted against a defined operator scope: number of tenants, included modules, catalog package, support level and intended deployment. A useful proposal names these boundaries so later expansion is a deliberate change instead of a surprise charge.",
    metric: "Recurring",
    metricLabel: "monthly license / quoted"
  },
  {
    id: "games",
    number: "02",
    icon: Gamepad2,
    accent: "violet",
    label: "OPTIONAL GAME ADDITIONS",
    title: "Put every added title on the schedule.",
    body: `Each tier begins with a specified package. For games beyond that package, the current default add-on is $${COMMERCIAL_MODEL.additionalGameMonthly} per additional title, per month, per tenant. The written catalog schedule should show exactly which games are included, which are extra and when billing starts.`,
    metric: `$${COMMERCIAL_MODEL.additionalGameMonthly}`,
    metricLabel: "additional title / tenant / month"
  },
  {
    id: "share",
    number: "03",
    icon: TrendingUp,
    accent: "green",
    label: "VARIABLE PARTICIPATION",
    title: "Define the revenue base before choosing a percentage.",
    body: `Standard commercial discussions use a negotiated ${COMMERCIAL_MODEL.revenueShareMin}–${COMMERCIAL_MODEL.revenueShareMax}% participation range. The agreement must identify the revenue base, allowable adjustments, reporting period and reconciliation method; the percentage alone cannot tell an operator what it will owe.`,
    metric: `${COMMERCIAL_MODEL.revenueShareMin}–${COMMERCIAL_MODEL.revenueShareMax}%`,
    metricLabel: "range subject to agreement"
  },
];

const comparisonRows = [
  ["License footprint", "1 tenant", "1 tenant", "1–multiple", "Negotiated"],
  ["Included game package", "Starter", "Expanded", "Large", "Custom / broad"],
  ["Add-on game rate", "$500/title/mo", "$500/title/mo", "$500/title/mo", "Negotiated"],
  ["Reporting depth", "Core", "Expanded", "Advanced", "Custom scope"],
  ["AI-assisted analysis", "—", "Optional / limited", "Available", "As contracted"],
  ["Tournament access", "—", "Optional", "Included access", "By catalog schedule"],
  ["Additional brands", "—", "—", "Available", "Negotiated"],
  ["Support arrangement", "Standard", "Enhanced", "Priority", "Custom SLA if agreed"],
  ["Participation range", "15–30%", "15–30%", "15–30%", "Negotiated"]
];

const faqItems = [
  {
    q: "Is there a published monthly price for each tier?",
    a: "Not yet. The page describes four licensing scopes, while subscription amounts are quoted after the tenant count, selected games, integrations, support requirements and deployment environment are defined. Ask for a written breakdown of recurring and one-time charges rather than relying on a headline tier name."
  },
  {
    q: "What is the difference between gross gaming revenue and the revenue-share base?",
    a: "Gross gaming revenue is commonly calculated from wagers less player winnings, but a contract may define the participation base differently. Ethosino has not specified deductible bonuses, chargebacks, taxes or processor fees on this page. The signed agreement must state the exact formula, data source, reporting period and dispute procedure."
  },
  {
    q: "When do the $500 additional-game charges apply?",
    a: "The current default is $500 for each licensed title beyond the included package, per month, per tenant. The game schedule should identify included versus additional titles, activation dates, any exceptions and how a title can be removed or changed. Do not assume prorating or cancellation terms until they are agreed."
  },
  {
    q: "Does the displayed percentage include the monthly license?",
    a: "No. The modeled 15–30% participation is separate from the quoted recurring license and any add-on titles. The simulator isolates that variable charge; it does not calculate a complete invoice or operator profit."
  },
  {
    q: "Can we agree to a pilot before taking a long-term package?",
    a: "A limited pilot can be discussed separately from these standard tier descriptions. Pilot duration, setup charges, monthly fees, participation, included games, conversion terms and exit conditions should appear in the written pilot agreement. Do not assume the standard tier economics automatically apply."
  },
  {
    q: "What external costs should we budget for?",
    a: "Budget separately for legal advice and any required approvals, payment processing, identity and KYC/AML providers, chargebacks, security or testing work, marketing, treasury needs and any hosting or integrations not included in the Ethosino quote. Your final proposal should identify which party contracts and pays each provider."
  },
  {
    q: "What should a formal proposal include?",
    a: "At minimum: operator entity and intended markets, tenant and brand count, included game schedule, module entitlements, monthly license, one-time work, revenue-share definition, add-on rates, provider responsibilities, support commitments, launch dependencies, change-order terms and termination provisions."
  },
  {
    q: "What happens when the operator outgrows its plan?",
    a: "An upgrade should be scoped by the additional capabilities required—not assumed from player count alone. Review extra game licenses, support coverage, new brands, infrastructure demand, integrations and reporting needs; then agree on the new price and effective date before enabling chargeable changes."
  }
];

const Reveal = ({ children, className = "", direction = "up", delay = 0 }) => {
  const reduceMotion = useReducedMotion();
  const map = {
    left: { x: -110, y: 0, rotate: -1.5 },
    right: { x: 110, y: 0, rotate: 1.5 },
    up: { x: 0, y: 70, rotate: 0 },
    topLeft: { x: -130, y: -85, rotate: -2.2 },
    topRight: { x: 130, y: -85, rotate: 2.2 },
    bottomLeft: { x: -130, y: 90, rotate: -2.2 },
    bottomRight: { x: 130, y: 90, rotate: 2.2 }
  };
  const start = map[direction] || map.up;

  return (
    <motion.div
      className={className}
      initial={
        reduceMotion
          ? false
          : {
              opacity: 0,
              ...start,
              scale: 0.965,
              filter: "blur(9px)"
            }
      }
      whileInView={{
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
        scale: 1,
        filter: "blur(0px)"
      }}
      viewport={{ once: true, amount: 0.14 }}
      transition={{
        duration: reduceMotion ? 0 : 0.78,
        delay,
        ease: [0.16, 1, 0.3, 1]
      }}
    >
      {children}
    </motion.div>
  );
};

const Spotlight = ({ children, className = "" }) => {
  const handlePointerMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty("--ep7-mx", `${event.clientX - rect.left}px`);
    event.currentTarget.style.setProperty("--ep7-my", `${event.clientY - rect.top}px`);
  };

  return (
    <div
      className={`eth-price-v7-spotlight ${className}`}
      onPointerMove={handlePointerMove}
    >
      <div className="eth-price-v7-spotlight-glow" />
      {children}
    </div>
  );
};

const PricingPageHelper = () => {
  const reduceMotion = useReducedMotion();
  const [activeTier, setActiveTier] = useState("standard");
  const [shareRate, setShareRate] = useState(20);
  const [monthlyGgr, setMonthlyGgr] = useState(100000);
  const [activeFaq, setActiveFaq] = useState(0);

  const selectedTier =
    pricingTiers.find((tier) => tier.id === activeTier) || pricingTiers[1];

  const SelectedTierIcon = selectedTier.icon;

  const economics = useMemo(() => {
    const safeGgr = Math.max(0, Number(monthlyGgr) || 0);
    const ethosinoShare = safeGgr * (shareRate / 100);

    return {
      safeGgr,
      ethosinoShare,
      operatorPreOtherCosts: safeGgr - ethosinoShare
    };
  }, [monthlyGgr, shareRate]);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 28,
    restDelta: 0.001
  });
  const heroY = useTransform(
    scrollYProgress,
    [0, 0.16],
    [0, reduceMotion ? 0 : 72]
  );
  const heroOpacity = useTransform(scrollYProgress, [0, 0.14], [1, 0.78]);

  return (
    <Fragment>
      <motion.div
        className="eth-price-v7-scroll-progress"
        style={{ scaleX: progress }}
      />

      <HeaderToMainPage />

      <main className="eth-price-v7-page eth-price-v7-readable">
        <div className="eth-price-v7-atmosphere" aria-hidden="true">
          <div className="eth-price-v7-grid" />
          <div className="eth-price-v7-noise" />
          <div className="eth-price-v7-orb eth-price-v7-orb--one" />
          <div className="eth-price-v7-orb eth-price-v7-orb--two" />
          <div className="eth-price-v7-orb eth-price-v7-orb--three" />
        </div>

        <section style={{ marginTop: 50 }} className="eth-price-v7-hero" id="pricing-top">
          <motion.div
            className="eth-price-v7-hero-parallax"
            style={{ y: heroY, opacity: heroOpacity }}
          >
            <div className="eth-price-v7-shell eth-price-v7-hero-grid">
              <div className="eth-price-v7-hero-copy">
                <motion.div
                  className="eth-price-v7-system-badge"
                  initial={reduceMotion ? false : { opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.58 }}
                >
                  <span className="eth-price-v7-live-dot" />
                                     OPERATOR LICENSING
                  <i />
                  TIER + CATALOG + PARTICIPATION
                </motion.div>

                <motion.h1
                  initial={
                    reduceMotion
                      ? false
                      : { opacity: 0, x: -90, y: 34, rotate: -1.8 }
                  }
                  animate={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: 0.05,
                    ease: [0.16, 1, 0.3, 1]
                  }}
                >
                  Know what you are buying.
                   <span>Know what changes the cost.</span>
                </motion.h1>

                <motion.p
                  className="eth-price-v7-hero-lead"
                  initial={reduceMotion ? false : { opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.72, delay: 0.14 }}
                >
                  Build a proposal around the number of brands you intend to run, the games your audience needs and the services your team will actually use. Compare the included scope, then distinguish <strong>ongoing software fees, optional catalog additions and negotiated participation</strong> in clear written terms.
                </motion.p>

                <motion.div
                  className="eth-price-v7-hero-thesis"
                  initial={reduceMotion ? false : { opacity: 0, x: -35 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.65, delay: 0.24 }}
                >
                  <Sparkles size={17} />
                  <span>
                    A tier is a starting point for a quote—not a substitute for an agreed scope, cost schedule and launch plan.
                  </span>
                </motion.div>

                <motion.div
                  className="eth-price-v7-hero-actions"
                  initial={reduceMotion ? false : { opacity: 0, y: 22 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.62, delay: 0.3 }}
                >
                  <a
                    className="eth-price-v7-btn eth-price-v7-btn--primary"
                    href="#operator-tiers"
                  >
                    Review licensing options
                    <ArrowRight size={17} />
                  </a>

                  <Link
                    className="eth-price-v7-btn eth-price-v7-btn--ghost"
                    to="/book-demo"
                  >
                    Request a tailored proposal
                  </Link>
                </motion.div>

                <div className="eth-price-v7-hero-proofline">
                  <span><ServerCog size={14} /> quoted platform license</span>
                  <span><Gamepad2 size={14} /> defined game packages</span>
                  <span><TrendingUp size={14} /> negotiated participation</span>
                  <span><Layers3 size={14} /> four licensing scopes</span>
                </div>
              </div>

              <motion.aside
                className="eth-price-v7-commercial-engine"
                initial={
                  reduceMotion
                    ? false
                    : {
                        opacity: 0,
                        x: 110,
                        y: -50,
                        rotate: 2.3,
                        scale: 0.95
                      }
                }
                animate={{ opacity: 1, x: 0, y: 0, rotate: 0, scale: 1 }}
                transition={{
                  duration: 0.92,
                  delay: 0.12,
                  ease: [0.16, 1, 0.3, 1]
                }}
              >
                <div className="eth-price-v7-commercial-engine-grid" />
                <div className="eth-price-v7-commercial-engine-scan" />

                <div className="eth-price-v7-engine-top">
                  <div>
                    <span className="eth-price-v7-live-dot" />
                    ILLUSTRATIVE COMMERCIAL STRUCTURE
                  </div>
                  <b>TERMS / SUBJECT TO AGREEMENT</b>
                </div>

                <div className="eth-price-v7-engine-orbit">
                  <motion.div
                    className="eth-price-v7-engine-ring eth-price-v7-engine-ring--outer"
                    animate={reduceMotion ? {} : { rotate: 360 }}
                    transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
                  />
                  <motion.div
                    className="eth-price-v7-engine-ring eth-price-v7-engine-ring--inner"
                    animate={reduceMotion ? {} : { rotate: -360 }}
                    transition={{ duration: 19, repeat: Infinity, ease: "linear" }}
                  />

                  <div className="eth-price-v7-engine-core">
                    <span className="eth-price-v7-core-wave" />
                    <CircleDollarSign size={27} />
                    <small>ETHOSINO</small>
                    <strong>ECONOMICS</strong>
                    <b>3 PRICING COMPONENTS</b>
                  </div>

                  <motion.div
                    className="eth-price-v7-engine-node eth-price-v7-engine-node--license"
                    animate={reduceMotion ? {} : { y: [0, -8, 0] }}
                    transition={{ duration: 4.6, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <ServerCog size={16} />
                    <div>
                      <span>PLATFORM</span>
                      <strong>MONTHLY</strong>
                    </div>
                  </motion.div>

                  <motion.div
                    className="eth-price-v7-engine-node eth-price-v7-engine-node--games"
                    animate={reduceMotion ? {} : { x: [0, 8, 0] }}
                    transition={{ duration: 5.2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <Gamepad2 size={16} />
                    <div>
                      <span>EXTRA GAMES</span>
                      <strong>${COMMERCIAL_MODEL.additionalGameMonthly}/MO</strong>
                    </div>
                  </motion.div>

                  <motion.div
                    className="eth-price-v7-engine-node eth-price-v7-engine-node--share"
                    animate={reduceMotion ? {} : { y: [0, 9, 0] }}
                    transition={{ duration: 4.9, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <TrendingUp size={16} />
                    <div>
                      <span>VARIABLE SHARE</span>
                      <strong>15–30%</strong>
                    </div>
                  </motion.div>

                  <span className="eth-price-v7-packet eth-price-v7-packet--one" />
                  <span className="eth-price-v7-packet eth-price-v7-packet--two" />
                  <span className="eth-price-v7-packet eth-price-v7-packet--three" />
                </div>

                <div className="eth-price-v7-engine-tiers">
                  {pricingTiers.map((tier, index) => (
                    <div
                      key={tier.id}
                      className={`eth-price-v7-engine-tier eth-price-v7-engine-tier--${tier.accent}`}
                    >
                      <span>0{index + 1}</span>
                      <strong>{tier.name.toUpperCase()}</strong>
                    </div>
                  ))}
                </div>
              </motion.aside>
            </div>
          </motion.div>

          <div className="eth-price-v7-marquee">
            <div className="eth-price-v7-marquee-track">
              {[
                "DEFINED LICENSE FOOTPRINT",
                 "WRITTEN GAME SCHEDULE",
                 "TRANSPARENT ADD-ON COSTS",
                 "NEGOTIATED REVENUE BASIS",
                 "THIRD-PARTY EXPENSES",
                 "SUPPORT SCOPE IN WRITING",
                 "PILOT TERMS AGREED SEPARATELY",
                 "PLANNED UPGRADE PATH",
                 "DEFINED LICENSE FOOTPRINT",
                 "WRITTEN GAME SCHEDULE",
                 "TRANSPARENT ADD-ON COSTS",
                 "NEGOTIATED REVENUE BASIS",
                 "THIRD-PARTY EXPENSES",
                 "SUPPORT SCOPE IN WRITING",
                 "PILOT TERMS AGREED SEPARATELY",
                 "PLANNED UPGRADE PATH"
              ].map((item, index) => (
                <React.Fragment key={`${item}-${index}`}>
                  <span>{item}</span>
                  <i>◆</i>
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <nav className="eth-price-v7-subnav">
          <div className="eth-price-v7-shell eth-price-v7-subnav-inner">
            <div className="eth-price-v7-subnav-brand">
              <span /> LICENSING & COMMERCIALS
            </div>
            <div className="eth-price-v7-subnav-links">
              <a href="#revenue-model">Cost model</a>
              <a href="#operator-tiers">Tiers</a>
              <a href="#economics-lab">Simulator</a>
              <a href="#comparison">Compare</a>
              <a href="#faq">FAQ</a>
              <Link className="eth-price-v7-subnav-cta" to="/book-demo">
                Request quote
              </Link>
            </div>
          </div>
        </nav>

        <section className="eth-price-v7-section" id="revenue-model">
          <div className="eth-price-v7-shell">
            <Reveal
              direction="topLeft"
              className="eth-price-v7-section-head eth-price-v7-section-head--split"
            >
              <div>
                <span className="eth-price-v7-kicker">WHAT CREATES A CHARGE</span>
                <h2>
                  Separate fixed costs, optional additions{" "}
                   <span>and variable participation.</span>
                </h2>
              </div>

              <p>
                An operator should be able to distinguish what is payable every month, what becomes payable only after requesting additional titles, and what varies with the agreed revenue base. These are different invoice components and should be documented separately.
              </p>
            </Reveal>

            <div className="eth-price-v7-revenue-grid">
              {revenueLayers.map((layer, index) => {
                const Icon = layer.icon;
                const directions = ["bottomLeft", "up", "bottomRight"];

                return (
                  <Reveal
                    key={layer.id}
                    direction={directions[index]}
                    delay={index * 0.07}
                  >
                    <Spotlight
                      className={`eth-price-v7-revenue-card eth-price-v7-revenue-card--${layer.accent}`}
                    >
                      <div className="eth-price-v7-revenue-card-top">
                        <span>{layer.number}</span>
                        <div><Icon size={20} /></div>
                      </div>

                      <small>{layer.label}</small>
                      <h3>{layer.title}</h3>
                      <p>{layer.body}</p>

                      <div className="eth-price-v7-revenue-metric">
                        <strong>{layer.metric}</strong>
                        <span>{layer.metricLabel}</span>
                      </div>
                    </Spotlight>
                  </Reveal>
                );
              })}
            </div>

            <Reveal direction="right" className="eth-price-v7-model-thesis">
              <div className="eth-price-v7-model-thesis-icon">
                <Waypoints size={20} />
              </div>
              <div>
                <span>HOW TO EVALUATE A QUOTE</span>
                <h3>
                  Compare total obligations, not just the headline license.
                </h3>
                <p>
                  Ask for the included game list, monthly platform amount, any one-time work, the exact revenue-share formula, external provider costs, support response terms and the conditions that change your bill. Those details make competing offers comparable.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section
          className="eth-price-v7-section eth-price-v7-section--panel"
          id="operator-tiers"
        >
          <div className="eth-price-v7-shell">
            <Reveal direction="topRight" className="eth-price-v7-section-head">
              <span className="eth-price-v7-kicker">LICENSING OPTIONS</span>
              <h2>
                Choose the scope that fits now.{" "}
                 <span>Quote the next step separately.</span>
              </h2>
              <p>
                The four plans distinguish initial breadth: how many brands, which games, which administrative capabilities and what level of assistance belong in the agreement. Select a plan to inspect its intended fit, then confirm each entitlement with a written schedule.
              </p>
            </Reveal>

            <div className="eth-price-v7-tier-selector">
              <Reveal direction="left" className="eth-price-v7-tier-tabs">
                {pricingTiers.map((tier) => {
                  const Icon = tier.icon;
                  return (
                    <motion.button
                      type="button"
                      key={tier.id}
                      className={[
                        "eth-price-v7-tier-tab",
                        `eth-price-v7-tier-tab--${tier.accent}`,
                        activeTier === tier.id ? "is-active" : ""
                      ].join(" ")}
                      onClick={() => setActiveTier(tier.id)}
                      whileHover={{ x: 5 }}
                      whileTap={{ scale: 0.985 }}
                    >
                      <div className="eth-price-v7-tier-tab-number">
                        {tier.number}
                      </div>
                      <div className="eth-price-v7-tier-tab-icon">
                        <Icon size={18} />
                      </div>
                      <div className="eth-price-v7-tier-tab-copy">
                        <span>{tier.label}</span>
                        <strong>{tier.name}</strong>
                      </div>
                      {tier.featured ? <b>FEATURED</b> : null}
                      <ArrowRight size={15} />
                    </motion.button>
                  );
                })}
              </Reveal>

              <Reveal direction="right" className="eth-price-v7-tier-detail-wrap">
                <AnimatePresence mode="wait">
                  <motion.article
                    key={selectedTier.id}
                    className={`eth-price-v7-tier-detail eth-price-v7-tier-detail--${selectedTier.accent}`}
                    initial={
                      reduceMotion
                        ? false
                        : {
                            opacity: 0,
                            x: 75,
                            y: -24,
                            rotate: 1.4,
                            filter: "blur(8px)"
                          }
                    }
                    animate={{
                      opacity: 1,
                      x: 0,
                      y: 0,
                      rotate: 0,
                      filter: "blur(0px)"
                    }}
                    exit={{
                      opacity: 0,
                      x: -55,
                      y: 24,
                      rotate: -1.2,
                      filter: "blur(6px)"
                    }}
                    transition={{ duration: 0.34 }}
                  >
                    <div className="eth-price-v7-tier-detail-top">
                      <div className="eth-price-v7-tier-detail-identity">
                        <div className="eth-price-v7-tier-detail-icon">
                          <SelectedTierIcon size={25} />
                        </div>
                        <div>
                          <span>{selectedTier.label}</span>
                          <h3>{selectedTier.name}</h3>
                        </div>
                      </div>

                      <div className="eth-price-v7-tier-quote">
                        <span>QUOTED MONTHLY LICENSE</span>
                        <strong>{selectedTier.monthly}</strong>
                        <small>final amount in proposal</small>
                      </div>
                    </div>

                    <p className="eth-price-v7-tier-summary">
                      {selectedTier.summary}
                    </p>

                    <div className="eth-price-v7-tier-positioning">
                      <Sparkles size={16} />
                      <span>{selectedTier.positioning}</span>
                    </div>

                    <div className="eth-price-v7-tier-metrics">
                      <div>
                        <span>INCLUDED CATALOG</span>
                        <strong>{selectedTier.games}</strong>
                      </div>
                      <div>
                        <span>BRAND / TENANT SCOPE</span>
                        <strong>{selectedTier.brands}</strong>
                      </div>
                      <div>
                        <span>SUPPORT</span>
                        <strong>{selectedTier.support}</strong>
                      </div>
                      <div>
                        <span>MODULE ACCESS</span>
                        <strong>{selectedTier.access}</strong>
                      </div>
                    </div>

                    <div className="eth-price-v7-tier-body-grid">
                      <div>
                        <div className="eth-price-v7-mini-heading">
                          SCOPE TO CONFIRM
                        </div>

                        <div className="eth-price-v7-feature-list">
                          {selectedTier.features.map((feature) => (
                            <div key={feature}>
                              <span>
                                <Check size={12} strokeWidth={3} />
                              </span>
                              {feature}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="eth-price-v7-mini-heading">TYPICAL BUYER</div>
                        <div className="eth-price-v7-fit-list">
                          {selectedTier.goodFor.map((fit, index) => (
                            <div key={fit}>
                              <span>0{index + 1}</span>
                              {fit}
                            </div>
                          ))}
                        </div>

                        <div className="eth-price-v7-tier-commercial">
                          <TrendingUp size={18} />
                          <div>
                            <span>VARIABLE PARTICIPATION</span>
                            <strong>
                              {COMMERCIAL_MODEL.revenueShareMin}–
                              {COMMERCIAL_MODEL.revenueShareMax}% negotiated
                            </strong>
                            <small>
                              Formula and rate belong in the signed terms.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="eth-price-v7-tier-actions">
                      <Link
                        className="eth-price-v7-btn eth-price-v7-btn--primary"
                        to="/book-demo"
                      >
                        Request {selectedTier.name} terms
                        <ArrowRight size={16} />
                      </Link>
                      <a
                        className="eth-price-v7-btn eth-price-v7-btn--ghost"
                        href="#comparison"
                      >
                        View the tier matrix
                      </a>
                    </div>
                  </motion.article>
                </AnimatePresence>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="eth-price-v7-section" id="economics-lab">
          <div className="eth-price-v7-shell">
            <Reveal
              direction="bottomLeft"
              className="eth-price-v7-section-head eth-price-v7-section-head--split"
            >
              <div>
                <span className="eth-price-v7-kicker">REVENUE-SHARE EXAMPLE</span>
                <h2>See how the share changes <span>with the agreed base.</span></h2>
              </div>
              <p>
                Enter an example monthly amount and adjust the participation percentage. The display calculates only a simple split of that input; it does not determine the contract definition of revenue, included deductions, payment timing or whether the operator is profitable.
              </p>
            </Reveal>

            <div className="eth-price-v7-economics-grid">
              <Reveal direction="left" className="eth-price-v7-economics-controls">
                <div className="eth-price-v7-economics-controls-head">
                  <CircleDollarSign size={18} />
                  <div>
                    <span>ILLUSTRATIVE SPLIT</span>
                    <strong>No quote or invoice generated</strong>
                  </div>
                </div>

                <label className="eth-price-v7-field">
                  <span>EXAMPLE MONTHLY SHARE BASE</span>
                  <div className="eth-price-v7-input-wrap">
                    <b>$</b>
                    <input
                      type="number"
                      min="0"
                      step="1000"
                      value={monthlyGgr}
                      onChange={(event) => setMonthlyGgr(event.target.value)}
                    />
                  </div>
                </label>

                <label className="eth-price-v7-field">
                  <div className="eth-price-v7-field-label-row">
                    <span>EXAMPLE PARTICIPATION RATE</span>
                    <strong>{shareRate}%</strong>
                  </div>

                  <input
                    className="eth-price-v7-range"
                    type="range"
                    min={COMMERCIAL_MODEL.revenueShareMin}
                    max={COMMERCIAL_MODEL.revenueShareMax}
                    step="1"
                    value={shareRate}
                    onChange={(event) =>
                      setShareRate(Number(event.target.value))
                    }
                  />

                  <div className="eth-price-v7-range-labels">
                    <span>{COMMERCIAL_MODEL.revenueShareMin}%</span>
                    <span>DISPLAYED NEGOTIATION RANGE</span>
                    <span>{COMMERCIAL_MODEL.revenueShareMax}%</span>
                  </div>
                </label>

                <div className="eth-price-v7-economics-warning">
                  <Scale size={17} />
                  <p>
                    This is arithmetic on the amount you enter, not a GGR verification or a profit forecast. Final terms must define qualifying revenue, adjustments, reporting and payment dates.
                  </p>
                </div>
              </Reveal>

              <Reveal direction="topRight" className="eth-price-v7-economics-output">
                <div className="eth-price-v7-economics-output-grid" />

                <div className="eth-price-v7-econ-output-top">
                  <span>EXAMPLE ONLY</span>
                  <b>{shareRate}% PARTICIPATION</b>
                </div>

                <div className="eth-price-v7-econ-big-number">
                  <span>REMAINDER BEFORE SEPARATE CHARGES</span>
                  <strong>
                    ${economics.operatorPreOtherCosts.toLocaleString(undefined, {
                      maximumFractionDigits: 0
                    })}
                  </strong>
                </div>

                <div className="eth-price-v7-econ-bars">
                  <div className="eth-price-v7-econ-bar-row">
                    <div>
                      <span>OPERATOR</span>
                      <b>{100 - shareRate}%</b>
                    </div>
                    <div className="eth-price-v7-econ-track">
                      <motion.div
                        className="eth-price-v7-econ-fill eth-price-v7-econ-fill--operator"
                        animate={{ width: `${100 - shareRate}%` }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                  </div>

                  <div className="eth-price-v7-econ-bar-row">
                    <div>
                      <span>ETHOSINO</span>
                      <b>{shareRate}%</b>
                    </div>
                    <div className="eth-price-v7-econ-track">
                      <motion.div
                        className="eth-price-v7-econ-fill eth-price-v7-econ-fill--ethosino"
                        animate={{ width: `${shareRate}%` }}
                        transition={{ duration: 0.4 }}
                      />
                    </div>
                  </div>
                </div>

                <div className="eth-price-v7-econ-ledger">
                  <div>
                    <span>USER-ENTERED SHARE BASE</span>
                    <strong>${economics.safeGgr.toLocaleString()}</strong>
                  </div>
                  <div>
                    <span>EXAMPLE ETHOSINO SHARE</span>
                    <strong>
                      ${economics.ethosinoShare.toLocaleString(undefined, {
                        maximumFractionDigits: 0
                      })}
                    </strong>
                  </div>
                  <div>
                    <span>MONTHLY LICENSE / QUOTED</span>
                    <strong>SEPARATE</strong>
                  </div>
                  <div>
                    <span>ADD-ON GAMES / IF SELECTED</span>
                    <strong>SEPARATE</strong>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="eth-price-v7-section eth-price-v7-section--panel">
          <div className="eth-price-v7-shell">
            <Reveal direction="topLeft" className="eth-price-v7-game-expansion">
              <div className="eth-price-v7-game-expansion-copy">
                <span className="eth-price-v7-kicker">ADDITIONAL GAME LICENSING</span>
                <h2>
                  Buy only the additional titles{" "}
                   <span>you plan to use.</span>
                </h2>
                <p>
                  The add-on rate is applied per title, per month and per tenant under the current default structure. Before activation, agree on the exact titles, any technical prerequisites and the effective billing date. Provider-controlled content may involve separate commercial terms.
                </p>

                <div className="eth-price-v7-game-price">
                  <strong>${COMMERCIAL_MODEL.additionalGameMonthly}</strong>
                  <span>per extra game · per month · per tenant</span>
                </div>

                <div className="eth-price-v7-game-expansion-note">
                  <LockKeyhole size={17} />
                  <p>
                    Request an itemized game schedule. It should distinguish included titles from paid additions and specify activation, changes, removals and any exceptions to the standard rate.
                  </p>
                </div>
              </div>

              <div className="eth-price-v7-catalog-visual">
                <div className="eth-price-v7-catalog-grid" />
                <div className="eth-price-v7-catalog-core">
                  <Gamepad2 size={25} />
                  <span>INCLUDED</span>
                  <strong>INCLUDED PACKAGE</strong>
                </div>

                {[
                  ["01", "PACKAGE TITLE", "INCLUDED"],
                  ["02", "PACKAGE TITLE", "INCLUDED"],
                  ["03", "PACKAGE TITLE", "INCLUDED"],
                  ["04", "ADD-ON", "$500 / MO"],
                  ["05", "ADD-ON", "$500 / MO"],
                  ["06", "ADD-ON", "$500 / MO"]
                ].map(([number, label, status], index) => (
                  <motion.div
                    key={`${number}-${label}`}
                    className={[
                      "eth-price-v7-catalog-node",
                      `eth-price-v7-catalog-node--${index + 1}`,
                      status === "INCLUDED" ? "is-included" : "is-addon"
                    ].join(" ")}
                    animate={
                      reduceMotion
                        ? {}
                        : {
                            y: [0, index % 2 === 0 ? -7 : 7, 0]
                          }
                    }
                    transition={{
                      duration: 4 + index * 0.22,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <span>{number}</span>
                    <div>
                      <small>{label}</small>
                      <strong>{status}</strong>
                    </div>
                  </motion.div>
                ))}

                <span className="eth-price-v7-catalog-packet eth-price-v7-catalog-packet--one" />
                <span className="eth-price-v7-catalog-packet eth-price-v7-catalog-packet--two" />
              </div>
            </Reveal>
          </div>
        </section>

        <section className="eth-price-v7-section" id="comparison">
          <div className="eth-price-v7-shell">
            <Reveal direction="bottomRight" className="eth-price-v7-section-head">
              <span className="eth-price-v7-kicker">AT-A-GLANCE COMPARISON</span>
              <h2>
                Compare the included scope{" "}
                 <span>before reviewing a quote.</span>
              </h2>
              <p>
                This matrix outlines intended access differences, not guaranteed pricing or committed service levels. Confirm the exact title list, access permissions, number of brands and support conditions in your individual proposal.
              </p>
            </Reveal>

            <Reveal direction="left" className="eth-price-v7-table-wrap">
              <table className="eth-price-v7-table">
                <thead>
                  <tr>
                    <th>CAPABILITY</th>
                    {pricingTiers.map((tier) => (
                      <th
                        key={tier.id}
                        className={tier.featured ? "is-featured" : ""}
                      >
                        <span>{tier.label}</span>
                        <strong>{tier.name}</strong>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row[0]}>
                      <td>{row[0]}</td>
                      <td>{row[1]}</td>
                      <td className="is-featured">{row[2]}</td>
                      <td>{row[3]}</td>
                      <td>{row[4]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Reveal>

            <Reveal direction="right" className="eth-price-v7-compare-foot">
              <BadgeCheck size={19} />
              <div>
                <span>CONTRACTUAL SOURCE OF TRUTH</span>
                <p>
                  Published scope labels are planning guides. The executed order form and referenced schedules govern actual entitlements, fees, changes, support commitments and the participation calculation.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="eth-price-v7-section eth-price-v7-section--panel">
          <div className="eth-price-v7-shell">
            <Reveal
              direction="topRight"
              className="eth-price-v7-section-head eth-price-v7-section-head--split"
            >
              <div>
                <span className="eth-price-v7-kicker">WHAT THE PROPOSAL SHOULD SPECIFY</span>
                <h2>
                  A complete quote tells you{" "}
                   <span>what is included and what is not.</span>
                </h2>
              </div>
              <p>
                Before selecting a provider, request an itemized statement of scope. These six details are what turn a discussion about feature access into a practical purchasing decision, a launch budget and a service agreement your team can administer.
              </p>
            </Reveal>

            <div className="eth-price-v7-value-grid">
              {[
                [Globe2, "LICENSE FOOTPRINT", "Brands & access", "Specify the number of brands, domains, operating environments and permitted staff seats or roles."],
                [Gamepad2, "CATALOG SCHEDULE", "Included versus extra", "List actual titles by name, classify add-ons and establish when each entitlement becomes billable."],
                [WalletCards, "INTEGRATION WORK", "Who connects what", "Assign responsibility for provider onboarding, merchant approval, technical implementation and any custom work."],
                [ShieldCheck, "OUTSIDE COSTS", "What is not included", "Identify legal, KYC/AML, payment, testing and other vendor expenses paid directly by the operator."],
                [ServerCog, "SERVICE COMMITMENTS", "Support expectations", "Document onboarding assistance, response coverage, maintenance notifications and any contracted service levels."],
                [Bot, "REPORTING & CHANGES", "How charges are reviewed", "Define revenue reports, reconciliation access, price-change notice, upgrade approvals and billing contacts."]
              ].map(([Icon, label, title, body], index) => {
                const directions = [
                  "topLeft",
                  "topRight",
                  "left",
                  "right",
                  "bottomLeft",
                  "bottomRight"
                ];

                return (
                  <Reveal
                    key={title}
                    direction={directions[index]}
                    delay={index * 0.045}
                  >
                    <Spotlight className="eth-price-v7-value-card">
                      <div className="eth-price-v7-value-card-top">
                        <span>0{index + 1}</span>
                        <Icon size={19} />
                      </div>
                      <small>{label}</small>
                      <h3>{title}</h3>
                      <p>{body}</p>
                    </Spotlight>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <section className="eth-price-v7-section">
          <div className="eth-price-v7-shell">
            <Reveal direction="bottomLeft" className="eth-price-v7-enterprise">
              <div className="eth-price-v7-enterprise-copy">
                <span className="eth-price-v7-kicker">CUSTOM OPERATING REQUIREMENTS</span>
                <h2>
                  Complex operations need{" "}
                   <span>a scoped commercial schedule.</span>
                </h2>
                <p>
                  An enterprise quote should identify the brands covered, desired hosting arrangement, integration ownership, review milestones and measurable service commitments. Dedicated resources, custom development and contractual guarantees are options to negotiate, not features to assume from a tier name.
                </p>

                <div className="eth-price-v7-enterprise-points">
                  <div><Network size={17} /><span>Named brands and environments in scope</span></div>
                  <div><Database size={17} /><span>Hosting and isolation requirements agreed</span></div>
                  <div><Gauge size={17} /><span>Capacity targets and review milestones</span></div>
                  <div><Boxes size={17} /><span>Provider integrations with assigned owners</span></div>
                </div>

                <Link
                  className="eth-price-v7-btn eth-price-v7-btn--primary"
                  to="/book-demo"
                >
                  Discuss a custom deployment
                  <ArrowRight size={16} />
                </Link>
              </div>

              <div className="eth-price-v7-enterprise-visual">
                <div className="eth-price-v7-enterprise-grid" />
                <div className="eth-price-v7-enterprise-core">
                  <Building2 size={27} />
                  <span>ENTERPRISE</span>
                  <strong>NEGOTIATED SCOPE</strong>
                </div>

                {[
                  ["TENANTS", "MULTI-BRAND", "cyan"],
                  ["CATALOG", "CUSTOM", "violet"],
                  ["INFRA", "DEDICATED", "green"],
                  ["SUPPORT", "IF CONTRACTED", "pink"]
                ].map(([label, value, accent], index) => (
                  <motion.div
                    key={label}
                    className={`eth-price-v7-enterprise-node eth-price-v7-enterprise-node--${index + 1} eth-price-v7-enterprise-node--${accent}`}
                    animate={
                      reduceMotion
                        ? {}
                        : { y: [0, index % 2 === 0 ? -8 : 8, 0] }
                    }
                    transition={{
                      duration: 4.2 + index * 0.35,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </motion.div>
                ))}

                <span className="eth-price-v7-enterprise-signal eth-price-v7-enterprise-signal--one" />
                <span className="eth-price-v7-enterprise-signal eth-price-v7-enterprise-signal--two" />
              </div>
            </Reveal>
          </div>
        </section>

        <section
          className="eth-price-v7-section eth-price-v7-section--panel"
          id="faq"
        >
          <div className="eth-price-v7-shell">
            <div className="eth-price-v7-faq-layout">
              <Reveal direction="topLeft" className="eth-price-v7-faq-copy">
                <span className="eth-price-v7-kicker">BUYER QUESTIONS</span>
                <h2>
                  Questions to settle{" "}
                   <span>before signing.</span>
                </h2>
                <p>
                  These answers are intended to help an operator evaluate an offer before launch. If a point affects invoice amounts, access, provider responsibility or the right to terminate, make sure it is documented in the agreement.
                </p>

                <div className="eth-price-v7-faq-callout">
                  <Gem size={20} />
                  <div>
                    <span>THE DOCUMENT TO ASK FOR</span>
                    <strong>
                      An itemized offer with included scope, revenue definition and third-party responsibilities.
                    </strong>
                  </div>
                </div>
              </Reveal>

              <Reveal direction="bottomRight" className="eth-price-v7-faq-list">
                {faqItems.map((item, index) => {
                  const open = activeFaq === index;

                  return (
                    <div
                      key={item.q}
                      className={[
                        "eth-price-v7-faq-item",
                        open ? "is-open" : ""
                      ].join(" ")}
                    >
                      <button
                        type="button"
                        onClick={() => setActiveFaq(open ? -1 : index)}
                      >
                        <span>{String(index + 1).padStart(2, "0")}</span>
                        <strong>{item.q}</strong>
                        <motion.div animate={{ rotate: open ? 180 : 0 }}>
                          <ChevronDown size={17} />
                        </motion.div>
                      </button>

                      <AnimatePresence initial={false}>
                        {open ? (
                          <motion.div
                            className="eth-price-v7-faq-answer"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.28 }}
                          >
                            <p>{item.a}</p>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </Reveal>
            </div>
          </div>
        </section>

        <section className="eth-price-v7-final">
          <div className="eth-price-v7-shell">
            <Reveal direction="bottomLeft" className="eth-price-v7-final-card">
              <div className="eth-price-v7-final-grid" />

              <div className="eth-price-v7-final-copy">
                <span>REQUEST A CLEAR COMMERCIAL PROPOSAL</span>
                <h2>
                  Know the scope. Understand the charges.{" "}
                   <strong>Then make the decision.</strong>
                </h2>
                <p>
                  Tell us how many brands you plan to operate, the markets you are considering, the games you want at launch and the integrations you require. We can use that scope to discuss a licensing tier and prepare a commercial proposal rather than asking you to guess at your total costs.
                </p>

                <div className="eth-price-v7-final-actions">
                  <Link
                    className="eth-price-v7-btn eth-price-v7-btn--primary eth-price-v7-btn--large"
                    to="/book-demo"
                  >
                    Request an itemized proposal
                    <ArrowRight size={17} />
                  </Link>

                  <a
                    className="eth-price-v7-btn eth-price-v7-btn--ghost eth-price-v7-btn--large"
                    href="#operator-tiers"
                  >
                    Return to plan comparison
                  </a>
                </div>
              </div>

              <div className="eth-price-v7-final-model">
                <div>
                  <span>01</span>
                  <strong>QUOTED PLATFORM LICENSE</strong>
                  <small>defined recurring scope</small>
                </div>
                <ArrowRight size={18} />
                <div>
                  <span>02</span>
                  <strong>OPTIONAL CATALOG ADDITIONS</strong>
                  <small>default $500 per extra title / month</small>
                </div>
                <ArrowRight size={18} />
                <div>
                  <span>03</span>
                  <strong>NEGOTIATED PARTICIPATION</strong>
                  <small>written revenue-base definition</small>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
    </Fragment>
  );
};

export default PricingPageHelper;
